<template>
  <div ref="validationRef">
    <div
      :class="{
        'flex items-start space-x-3' : col,
        'flex-row-reverse justify-end space-x-3 space-x-reverse' : col && reverse,
      }"
    >
      <div
        :class="{
          // col && !reverse ? 'w-3/12' : 'w-full'
          'w-full' : !col,
          'w-3/12' : col && !reverse,
        }"
      >
        <div class="w-full flex justify-between items-center">
          <label v-if="label" :class="labelClass" tabindex="-1">
            <span v-html="label" />
            <o-tooltip v-if="isRequired" :text="$t('components.elements.fieldGroup.required')" class="inline-block">
              <span class="text-sm text-red-400 dark:text-red-600">
                *
              </span>
            </o-tooltip>

            <o-tooltip v-if="tooltip" :text="tooltip" class="inline-block translate-y-[0.2rem]">
              <div class="h-5 w-5 rounded-full text-blue-500 flex items-center justify-center">
                <o-icon icon="info" />
              </div>
            </o-tooltip>
          </label>
          <slot name="control">
            <o-tooltip v-if="modified" :text="modifiedMessage">
              <o-icon icon="warningBold" fill class="text-yellow-600 mx-1 dark:text-yellow-400" />
            </o-tooltip>
          </slot>
        </div>
        <p v-if="info" :class="infoClass" v-html="info" />
      </div>
      <div
        :class="{
          // col && !reverse ? 'w-3/12' : 'w-full'
          'w-full' : !col,
          'w-9/12' : col && !reverse,
        }"
      >
        <div ref="fields" :class="[{ 'mt-1' : label || info }]">
          <slot v-bind="slotProps" />
        </div>
        <div v-if="message || (isError && errorMessage) || (isSuccess && successMessage)" :class="statusClasses">
          <o-icon :icon="iconName" :size="14" class="ml-1 mr-2" />
          <p class="leading-none pt-px" v-html="errorMessage || message || successMessage" />
        </div>
        <div v-else-if="date && dateFormat">
          <p class="text-sm opacity-80 mt-1">
            {{ dateFormat }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
export default { name: 'OFieldGroup' }
</script>

<script setup lang="ts">
import { isVNode, type VNode } from 'vue'
import { useField, type RuleExpression } from 'vee-validate'
import isEqual from 'lodash/isEqual'

const { $i18n } = useNuxtApp()
const slots = useSlots()

interface Props {
  label?: string | null
  info?: string | null
  tooltip?: string | null
  name?: string | null
  rules?: string | object
  message?: string | null
  successMessage?: string | null
  col?: boolean
  reverse?: boolean
  showSuccess?: boolean
  status?: boolean | string | null
  modified?: boolean
  modifiedMessage?: string
  labelClass?: string
  infoClass?: string
  statusClass?: string
  errorClass?: string
  successClass?: string
  warningClass?: string
  date?: boolean
  validationValue?: unknown
}

const props = withDefaults(defineProps<Props>(), {
  label: null,
  info: null,
  tooltip: null,
  name: null,
  rules: '',
  message: null,
  successMessage: null,
  col: false,
  reverse: false,
  showSuccess: false,
  status: null,
  modified: false,
  modifiedMessage: undefined,
  labelClass: 'block text-sm font-medium float-left',
  infoClass: 'block opacity-80 text-sm',
  statusClass: 'flex items-center text-gray-600 text-sm pt-2 pb-1 -ml-2px leading-none opacity-85 dark:text-gray-300',
  errorClass: 'text-red-500',
  successClass: 'text-green-500',
  warningClass: 'text-orange-400 dark:text-orange-600',
  date: false,
})

// Raw VNode props distinguish an explicitly-bound undefined value (which must
// clear stale validation state) from legacy field groups that omit this prop.
const vnodeProps = getCurrentInstance()?.vnode.props ?? {}
const hasExplicitValidationValue = ['validationValue', 'validation-value']
  .some(key => Object.prototype.hasOwnProperty.call(vnodeProps, key))

// Shadow the prop to provide i18n default
const modifiedMessage = computed(() =>
  props.modifiedMessage ?? $i18n.t('components.elements.fieldGroup.modified') as string,
)

const validationRef = ref<any>(null)
const fields = ref<any>()
const generatedName = useId()

const fieldName = computed(() => props.name || generatedName)
const fieldRules = computed<RuleExpression<unknown>>(() => props.rules as RuleExpression<unknown>)

function findSlotValue(nodes: VNode[] | undefined): { found: boolean, value?: unknown } {
  for (const node of nodes || []) {
    if (!isVNode(node)) continue

    if (node.props && Object.prototype.hasOwnProperty.call(node.props, 'modelValue')) {
      return { found: true, value: node.props.modelValue }
    }

    const directive = (node as VNode & { dirs?: Array<{ value: unknown }> }).dirs?.[0]
    if (directive) {
      return { found: true, value: directive.value }
    }

    if (Array.isArray(node.children)) {
      const nested = findSlotValue(node.children as VNode[])
      if (nested.found) return nested
    }
  }

  return { found: false }
}

const { errorMessage, meta, setValue, value: fieldValue } = useField(fieldName, fieldRules, {
  label: props.name || props.label || undefined,
  validateOnValueUpdate: false,
})

function syncFieldValue() {
  if (hasExplicitValidationValue) return

  const slotValue = findSlotValue(slots.default?.(slotProps.value))

  if (slotValue.found && !isEqual(slotValue.value, fieldValue.value)) {
    setValue(slotValue.value, false)
  }
}

onMounted(syncFieldValue)
onBeforeUpdate(syncFieldValue)
watch(() => props.validationValue, (value) => {
  if (hasExplicitValidationValue && !Object.is(value, fieldValue.value)) {
    setValue(value, false)
  }
}, { immediate: true })

const dateFormat = computed(() => {
  if (!props.date) return ''
  const locale = $i18n.locale.value || 'en'
  // Format a sample date to derive the locale pattern
  const sample = new Date(2000, 0, 15) // Jan 15, 2000
  const formatted = new Intl.DateTimeFormat(locale, { year: 'numeric', month: '2-digit', day: '2-digit' }).format(sample)
  // Convert formatted date back to pattern by replacing known values
  return formatted.replace('2000', 'YYYY').replace('01', 'MM').replace('15', 'DD')
})

const statusClasses = computed(() => {
  const classes = [props.statusClass]
  if (isError.value && props.errorClass) {
    classes.push(props.errorClass)
  } else if (isSuccess.value && props.successClass) {
    classes.push(props.successClass)
  } else if (isWarning.value && props.warningClass) {
    classes.push(props.warningClass)
  }
  return classes
})

const isSuccess = computed(() => {
  return (props.status === true || props.status === 'success' || (meta.valid && meta.dirty)) && props.showSuccess
})

const isError = computed(() => {
  return props.status === false || props.status === 'error' || Boolean(errorMessage.value)
})

const isWarning = computed(() => {
  return props.status === 'warning'
})

const isRulesString = computed(() => {
  return typeof props.rules === 'string'
})

const isRequired = computed(() => {
  if (!props.rules) {
    return false
  }
  const rules = isRulesString.value ? props.rules as string : Object.keys(props.rules as object)
  return rules.includes('required')
})

const iconName = computed(() => {
  if (isError.value) {
    return 'danger'
  }
  if (isSuccess.value) {
    return 'success'
  }
  if (isWarning.value) {
    return 'warning'
  }
  return 'info'
})

const slotProps = computed(() => {
  let status: string | undefined
  if (isSuccess.value) {
    status = 'success'
  }
  if (isWarning.value) {
    status = 'warning'
  }
  if (isError.value) {
    status = 'error'
  }
  return { status }
})

</script>
